/**
 * masterGanttSummary.ts
 * Phase 4, Item 6 — Pure helper: compute right-rail summary totals
 * from a MasterGanttResponse without touching the DOM or making API calls.
 *
 * Determinism rule: given the same response object, this function always
 * returns the same output. No side-effects.
 *
 * WO classification logic:
 *   - A WO is "confirmed" when it has no Tentative machineBookings AND no
 *     Tentative employeeAssignments (or has zero bookings/assignments, in
 *     which case it is also considered confirmed — not yet scheduled but
 *     not tentative either).
 *   - A WO is "proposed" when it has at least one Tentative booking or
 *     assignment.
 *
 * This is intentionally simpler than "count distinct ScheduleTaskKeys per
 * status" to avoid needing task-level status (not present on
 * MasterGanttResponse bookings).
 */

import type { MasterGanttResponse } from '../../../types/schedule.js';

// ---------------------------------------------------------------------------
// Output shape
// ---------------------------------------------------------------------------

export interface MasterGanttSummary {
  /** Total distinct projects in the loaded response. */
  projects: number;
  /** Work Orders where no booking or assignment is Tentative. */
  wosConfirmed: number;
  /** Work Orders with at least one Tentative booking or assignment. */
  wosProposed: number;
  /** Distinct machine codes referenced across all MachineBookings. */
  machines: number;
  /** Distinct user keys referenced across all EmployeeAssignments. */
  employees: number;
  /**
   * Distinct non-null scheduleTaskKey values referenced in MachineBookings.
   * Each represents a ScheduleTask that has been placed on at least one machine
   * — i.e., "scheduled."
   */
  tasksScheduled: number;
}

// ---------------------------------------------------------------------------
// Core function
// ---------------------------------------------------------------------------

/**
 * computeMasterGanttSummary
 *
 * @param response - The loaded MasterGanttResponse (may be null while loading)
 * @returns Summary counts; all zeros when response is null.
 */
export function computeMasterGanttSummary(
  response: MasterGanttResponse | null,
): MasterGanttSummary {
  const zero: MasterGanttSummary = {
    projects: 0,
    wosConfirmed: 0,
    wosProposed: 0,
    machines: 0,
    employees: 0,
    tasksScheduled: 0,
  };

  if (!response) return zero;

  let wosConfirmed = 0;
  let wosProposed = 0;
  const machineKeys = new Set<string>();
  const userKeys = new Set<string>();
  const scheduledTaskKeys = new Set<string>();

  for (const project of response.projects) {
    for (const wo of project.workOrders) {
      // Classify WO as proposed if any booking or assignment is Tentative
      const hasTentative =
        wo.machineBookings.some((b) => b.status === 'Tentative') ||
        wo.employeeAssignments.some((a) => a.status === 'Tentative');

      if (hasTentative) {
        wosProposed++;
      } else {
        wosConfirmed++;
      }

      // Collect distinct machines
      for (const b of wo.machineBookings) {
        machineKeys.add(b.machineKey);

        // Count task as "scheduled" when it has a booking
        if (b.scheduleTaskKey) {
          scheduledTaskKeys.add(b.scheduleTaskKey);
        }
      }

      // Collect distinct employees
      for (const a of wo.employeeAssignments) {
        userKeys.add(a.userKey);
      }
    }
  }

  return {
    projects: response.projects.length,
    wosConfirmed,
    wosProposed,
    machines: machineKeys.size,
    employees: userKeys.size,
    tasksScheduled: scheduledTaskKeys.size,
  };
}
